import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitBranch } from 'lucide-react';
import type { AdapterView, ConnectionView, CredentialView } from '@agentic-os/contracts';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button, ErrorNote, Mono, PageHeader, Table, Td, Th } from '@/components/ui/primitives';
import { DomainStatus } from '@/components/ui/status-badge';
import { api, formatTime } from '@/lib/api';
import { useSession } from '@/lib/session';

const input = 'w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm';
const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'] as const;

/**
 * Connections (ADR-010): a name + capability bindings (native adapter or approved MCP tool) +
 * a credential reference + the highest classification its data may carry. Workflows only
 * ever reference the connection name and capability.
 */
export function ConnectionsPage() {
  const { can } = useSession();
  const manage = can('connections.manage');
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['connections'],
    queryFn: () => api<{ items: ConnectionView[]; adapters: AdapterView[] }>('/api/v1/connections'),
  });
  const creds = useQuery({
    queryKey: ['credentials'],
    queryFn: () => api<{ items: CredentialView[] }>('/api/v1/credentials'),
    enabled: manage,
  });
  const adapters = list.data?.adapters ?? [];
  const empty = {
    name: '',
    description: '',
    adapter: '',
    mcpBinding: '',
    config: '{}',
    credentialId: '',
    classification: 'INTERNAL',
    allowedHosts: '',
  };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState<ConnectionView | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['connections'] });

  const save = useMutation({
    mutationFn: (body: unknown) =>
      editing
        ? api<ConnectionView>(`/api/v1/connections/${editing.id}`, { method: 'PUT', body })
        : api<ConnectionView>('/api/v1/connections', { method: 'POST', body }),
    onSuccess: () => {
      setForm(empty);
      setEditing(null);
      refresh();
    },
  });
  /** Loads a connection into the form (the name cannot change; everything else can). */
  const startEdit = (c: ConnectionView) => {
    const entries = Object.entries(c.bindings);
    const native = entries
      .find(([, impl]) => impl.startsWith('native:'))?.[1]
      .slice('native:'.length);
    const mcp = entries.find(([, impl]) => !impl.startsWith('native:'));
    setEditing(c);
    setFormError(null);
    save.reset();
    setForm({
      name: c.name,
      description: c.description,
      adapter: native ?? '',
      mcpBinding: mcp ? `${mcp[0]} = ${mcp[1]}` : '',
      config: JSON.stringify(c.config, null, 2),
      credentialId: c.credentialId ?? '',
      classification: c.classification,
      allowedHosts: c.allowedHosts.join(', '),
    });
    document.getElementById('connection-form')?.scrollIntoView({ behavior: 'smooth' });
  };
  const cancelEdit = () => {
    setEditing(null);
    setForm(empty);
    setFormError(null);
  };
  const toggle = useMutation({
    mutationFn: (c: ConnectionView) =>
      api<ConnectionView>(`/api/v1/connections/${c.id}`, {
        method: 'PUT',
        body: {
          name: c.name,
          description: c.description,
          bindings: c.bindings,
          config: c.config,
          credentialId: c.credentialId,
          classification: c.classification,
          allowedHosts: c.allowedHosts,
          status: c.status === 'active' ? 'disabled' : 'active',
        },
      }),
    onSuccess: refresh,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    let config: unknown;
    try {
      config = JSON.parse(form.config || '{}');
    } catch {
      setFormError('Configuration must be valid JSON.');
      return;
    }
    const adapter = adapters.find((a) => a.id === form.adapter);
    const bindings: Record<string, string> = {};
    if (adapter) for (const cap of adapter.capabilities) bindings[cap] = `native:${adapter.id}`;
    if (form.mcpBinding) {
      const [cap, impl] = form.mcpBinding.split('=').map((s) => s.trim());
      if (cap && impl) bindings[cap] = impl;
    }
    save.mutate({
      name: form.name,
      description: form.description,
      bindings,
      config,
      credentialId: form.credentialId || null,
      classification: form.classification,
      allowedHosts: form.allowedHosts
        .split(',')
        .map((h) => h.trim())
        .filter(Boolean),
      ...(editing ? { status: editing.status } : {}),
    });
  };

  const selected = adapters.find((a) => a.id === form.adapter);
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Connections"
        description="Named connections map capabilities (e.g. issue.read) to an implementation. Workflows reference only the connection name."
      />
      {manage && (
        <Card id="connection-form">
          <CardHeader>
            <CardTitle>{editing ? `Edit connection ${editing.name}` : 'Add connection'}</CardTitle>
          </CardHeader>
          <CardBody>
            <form onSubmit={submit} className="grid gap-3 md:grid-cols-3" autoComplete="off">
              <label className="text-xs text-muted">
                Name
                <input
                  className={input}
                  required
                  readOnly={!!editing}
                  aria-describedby={editing ? 'connection-name-fixed' : undefined}
                  pattern="[a-z0-9][a-z0-9._\-]{1,62}"
                  placeholder="issue-tracker"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted">
                Native adapter
                <select
                  className={input}
                  value={form.adapter}
                  onChange={(e) => setForm({ ...form, adapter: e.target.value })}
                >
                  <option value="">— none (MCP binding only) —</option>
                  {adapters.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.id}
                      {a.synthetic ? ' (SYNTHETIC)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs text-muted">
                Highest data classification
                <select
                  className={input}
                  value={form.classification}
                  onChange={(e) => setForm({ ...form, classification: e.target.value })}
                >
                  {CLASSIFICATIONS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>
              {selected && (
                <p className="text-xs text-muted md:col-span-3">
                  {selected.description} Capabilities:{' '}
                  <Mono>{selected.capabilities.join(', ')}</Mono>
                  {selected.requiresCredential && ' · requires a credential'}
                </p>
              )}
              <label className="text-xs text-muted">
                Credential
                <select
                  className={input}
                  value={form.credentialId}
                  onChange={(e) => setForm({ ...form, credentialId: e.target.value })}
                >
                  <option value="">— none —</option>
                  {(creds.data?.items ?? [])
                    .filter((c) => c.status === 'active')
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </label>
              <label className="text-xs text-muted">
                Allowed hosts (comma-separated, optional)
                <input
                  className={input}
                  placeholder="tracker.example.com"
                  value={form.allowedHosts}
                  onChange={(e) => setForm({ ...form, allowedHosts: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted">
                MCP binding (optional)
                <input
                  className={input}
                  placeholder="issue.read = mcp:server/tool"
                  value={form.mcpBinding}
                  onChange={(e) => setForm({ ...form, mcpBinding: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted md:col-span-2">
                Adapter configuration (JSON, no secrets)
                <textarea
                  className={`${input} font-mono`}
                  rows={3}
                  value={form.config}
                  onChange={(e) => setForm({ ...form, config: e.target.value })}
                />
              </label>
              <label className="text-xs text-muted">
                Description
                <input
                  className={input}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </label>
              <div className="flex flex-wrap items-center gap-2 md:col-span-3">
                <Button type="submit" variant="primary" disabled={save.isPending}>
                  <GitBranch className="size-3.5" aria-hidden />
                  {editing ? 'Save changes' : 'Save connection'}
                </Button>
                {editing && (
                  <>
                    <Button onClick={cancelEdit}>Cancel</Button>
                    <span id="connection-name-fixed" className="text-xs text-muted">
                      The name is fixed (workflows reference it). Saving keeps the current status.
                    </span>
                  </>
                )}
              </div>
            </form>
            {formError && (
              <p role="alert" className="mt-3 text-sm text-fail">
                {formError}
              </p>
            )}
            {save.isError && (
              <div className="mt-3">
                <ErrorNote error={save.error} />
              </div>
            )}
          </CardBody>
        </Card>
      )}
      {toggle.isError && <ErrorNote error={toggle.error} />}
      <Card>
        <CardBody className="p-0">
          {list.isError ? (
            <div className="p-4">
              <ErrorNote error={list.error} />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Bindings</Th>
                  <Th>Credential</Th>
                  <Th>Classification</Th>
                  <Th>Status</Th>
                  <Th>Updated</Th>
                  {manage && <Th>Actions</Th>}
                </tr>
              </thead>
              <tbody>
                {(list.data?.items ?? []).map((c) => (
                  <tr key={c.id}>
                    <Td>
                      {c.name}
                      {c.description && <p className="text-xs text-muted">{c.description}</p>}
                    </Td>
                    <Td>
                      {Object.entries(c.bindings).map(([cap, impl]) => (
                        <div key={cap}>
                          <Mono>{`${cap} → ${impl}`}</Mono>
                        </div>
                      ))}
                    </Td>
                    <Td>{c.credentialName ?? '—'}</Td>
                    <Td>{c.classification}</Td>
                    <Td>
                      <DomainStatus value={c.status} />
                    </Td>
                    <Td className="text-muted">{formatTime(c.updatedAt)}</Td>
                    {manage && (
                      <Td>
                        <div className="flex gap-2">
                          <Button onClick={() => startEdit(c)} aria-label={`Edit ${c.name}`}>
                            Edit
                          </Button>
                          <Button onClick={() => toggle.mutate(c)}>
                            {c.status === 'active' ? 'Disable' : 'Enable'}
                          </Button>
                        </div>
                      </Td>
                    )}
                  </tr>
                ))}
                {list.data?.items.length === 0 && (
                  <tr>
                    <Td colSpan={manage ? 7 : 6} className="text-muted">
                      No connections yet.
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
